import {
    Box,
    Button,
    Flex,
    SimpleGrid,
    Skeleton,
    Text,
    useColorModeValue,
    Icon,
} from "@chakra-ui/react";
import StationsApi from "Api/StationsApi";
import Favorites from "Helpers/Favorites";
import { useLoading } from "Providers/LoadingProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaSortAlphaDown, FaSortAmountDown, FaSortAmountDownAlt, FaThermometerHalf, FaTint, FaStar, FaCalendarAlt, FaChartBar } from "react-icons/fa";
import Ranking from "./Components/Ranking";
import RankingRain from "./Components/RankingRain";
import Widget from "./Components/Widget";
import VirtualStationGrid from "./Components/VirtualStationGrid";
import Footer from "components/Footer/Footer";

const brandGreen = "#2D6B3F";
const brandGreenDark = "#1F5A2F";

function orderStations(list, order) {
    if (order === 'temp_asc' || order === 'temp_desc') {
        list.sort((stationA, stationB) => {
            const aTemp = stationA.last_data?.temp;
            const bTemp = stationB.last_data?.temp;
            const aHas = aTemp != null;
            const bHas = bTemp != null;
            // Stations without a reading sort to the end, consistently.
            if (!aHas && !bHas) return 0;
            if (!aHas) return 1;
            if (!bHas) return -1;
            return aTemp - bTemp;
        });
        if (order === 'temp_desc') {
            list.reverse();
        }
        return list;
    } else {
        return list.sort((a, b) => a.city.localeCompare(b.city));
    }
}

// Sort button styling
function SortButton({ icon, isActive, onClick, children, bgInactive, textInactive, borderColor, hoverInactive }) {
    return (
        <Button
            leftIcon={icon}
            onClick={onClick}
            size={{ base: "sm", md: "md" }}
            bg={isActive ? brandGreen : bgInactive}
            color={isActive ? "white" : textInactive}
            border="1px solid"
            borderColor={isActive ? brandGreen : borderColor}
            _hover={{
                bg: isActive ? brandGreenDark : hoverInactive,
                transform: "translateY(-1px)",
            }}
            transition="all 0.2s ease"
            fontWeight="medium"
            borderRadius="lg"
        >
            {children}
        </Button>
    );
}

// Section header component
function SectionHeader({ icon, title, subtitle, color = brandGreen }) {
    return (
        <Box
            bg={`linear-gradient(135deg, ${color} 0%, ${brandGreenDark} 100%)`}
            borderRadius="xl"
            p={{ base: 4, md: 5 }}
            mb={4}
            boxShadow="0 4px 15px rgba(45, 107, 63, 0.2)"
        >
            <Flex align="center" gap={3}>
                <Flex
                    align="center"
                    justify="center"
                    bg="whiteAlpha.200"
                    borderRadius="lg"
                    p={2}
                >
                    <Icon as={icon} color="white" boxSize={5} />
                </Flex>
                <Box>
                    <Text color="white" fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>
                        {title}
                    </Text>
                    {subtitle && (
                        <Text color="whiteAlpha.800" fontSize="sm">
                            {subtitle}
                        </Text>
                    )}
                </Box>
            </Flex>
        </Box>
    );
}

export default function Estacoes() {
    const loading = useLoading();
    const [rankings, setRankings] = useState({});
    const [isLoadingStations, setIsLoadingStations] = useState(true);
    const [stations, setStations] = useState([]);
    // const [currentStation, setCurrentStation] = useState(null);
    const [order, setOrder] = useState('temp_asc');
    const [favorites, setFavorites] = useState(Favorites.get());
    // const { isOpen: isStationModalOpen, onOpen: onStationModalOpen, onClose: onStationModalClose } = useDisclosure();

    useEffect( () => {
        loading.hide();
        loadStations();
        loadRankings();
    }, []);

    useEffect(() => {
        var channel = window.pusher.subscribe('stations');
        channel.bind('stations-update', function() {
            loadStations(false);
        });

        return () => {
            window.pusher.unsubscribe('stations');
        }
    }, []);

    async function loadStations(showLoading = true) {
        showLoading && setIsLoadingStations(true);
        let data = await StationsApi.homepage();
        setStations(data.json || []);
        showLoading && setIsLoadingStations(false);
    }

    async function loadRankings() {
        let data = await StationsApi.ranking();
        setRankings(data.json.data || []);
    }

    const toggleFavorite = useCallback((station) => {
        setFavorites((current) => (
            current.includes(station.identifier)
                ? current.filter((f) => f !== station.identifier)
                : [...current, station.identifier]
        ));
    }, []);

    useEffect( () => {
        Favorites.set(favorites);
    }, [favorites]);

    const favoriteStations = useMemo(() => {
        return orderStations((stations || []).filter( station => {
            return favorites.includes(station.identifier) && station.last_data && station.last_data.temp != null;
        }), order);
    }, [stations, favorites, order]);

    const nonFavorites = useMemo(() => {
        return orderStations((stations || []).filter( station => {
            return !favorites.includes(station.identifier) && station.last_data && station.last_data.temp != null;
        }), order);
    }, [stations, favorites, order]);

    // function openStationModal(station) {
    //     setCurrentStation(station);
    //     onStationModalOpen();
    // }

    // Theme colors
    const cardBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const textColor = useColorModeValue("gray.800", "white");
    const buttonBgInactive = useColorModeValue("white", "gray.700");
    const buttonTextInactive = useColorModeValue("gray.700", "gray.200");
    const buttonHoverInactive = useColorModeValue("gray.100", "gray.600");

    return (
        <Box px={{ base: 2, md: 4 }} maxW="1800px" mx="auto">
            {/* Sort Controls */}
            <Box
                bg={cardBg}
                borderRadius="xl"
                p={{ base: 3, md: 4 }}
                mb={6}
                boxShadow="sm"
                border="1px solid"
                borderColor={borderColor}
            >
                <Flex
                    justifyContent="center"
                    direction={{ base: "column", md: "row" }}
                    gap={2}
                >
                    <SortButton
                        icon={<FaSortAmountDownAlt />}
                        isActive={order === 'temp_asc'}
                        onClick={() => setOrder('temp_asc')}
                        bgInactive={buttonBgInactive}
                        textInactive={buttonTextInactive}
                        borderColor={borderColor}
                        hoverInactive={buttonHoverInactive}
                    >
                        Menor → Maior
                    </SortButton>
                    <SortButton
                        icon={<FaSortAmountDown />}
                        isActive={order === 'temp_desc'}
                        onClick={() => setOrder('temp_desc')}
                        bgInactive={buttonBgInactive}
                        textInactive={buttonTextInactive}
                        borderColor={borderColor}
                        hoverInactive={buttonHoverInactive}
                    >
                        Maior → Menor
                    </SortButton>
                    <SortButton
                        icon={<FaSortAlphaDown />}
                        isActive={order === 'city_name'}
                        onClick={() => setOrder('city_name')}
                        bgInactive={buttonBgInactive}
                        textInactive={buttonTextInactive}
                        borderColor={borderColor}
                        hoverInactive={buttonHoverInactive}
                    >
                        Cidade (A-Z)
                    </SortButton>
                </Flex>
            </Box>

            {/* Favorites Section */}
            {favorites?.length ? (
                <Box mb={6}>
                    <SectionHeader
                        icon={FaStar}
                        title="Favoritos"
                        subtitle={`${favoriteStations.length} estações`}
                        color="#D69E2E"
                    />
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={4}>
                        {favoriteStations.map(station => (
                            <Widget
                                key={station.id}
                                station={station}
                                isFavorite={true}
                                toggleFavorite={toggleFavorite}
                            />
                        ))}
                    </SimpleGrid>
                </Box>
            ) : null}

            {/* All Stations Section */}
            {nonFavorites.length ? (
                <Box mb={8}>
                    {favorites.length > 0 && (
                        <SectionHeader
                            icon={FaThermometerHalf}
                            title="Outras Estações"
                            subtitle={`${nonFavorites.length} estações disponíveis`}
                        />
                    )}
                    <VirtualStationGrid
                        stations={nonFavorites}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                    />
                </Box>
            ) : null}

            {/* Loading Skeletons */}
            {isLoadingStations && (
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 6 }} spacing={4} mb={8}>
                    {[...Array(8).keys()].map(i => (
                        <Skeleton key={i} height={280} borderRadius="xl" />
                    ))}
                </SimpleGrid>
            )}

            {/* Daily Ranking Section */}
            <Box mb={8}>
                <SectionHeader
                    icon={FaChartBar}
                    title="Ranking Diário"
                    subtitle={(new Date()).toLocaleDateString('pt-BR')}
                />
                <Box
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={borderColor}
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }}>
                        <Box p={4} borderRight={{ md: "1px solid" }} borderColor={borderColor}>
                            <Flex align="center" gap={2} mb={3}>
                                <Box w="4px" h="20px" bg="blue.400" borderRadius="full" />
                                <Text fontWeight="semibold" color={textColor}>Mínimas</Text>
                            </Flex>
                            <Ranking stations={rankings?.day_min} />
                        </Box>
                        <Box p={4}>
                            <Flex align="center" gap={2} mb={3}>
                                <Box w="4px" h="20px" bg="red.400" borderRadius="full" />
                                <Text fontWeight="semibold" color={textColor}>Máximas</Text>
                            </Flex>
                            <Ranking stations={rankings?.day_max} />
                        </Box>
                    </SimpleGrid>
                </Box>
            </Box>

            {/* Annual Ranking Section */}
            <Box mb={8}>
                <SectionHeader
                    icon={FaCalendarAlt}
                    title="Ranking Anual"
                    subtitle={(new Date()).getFullYear().toString()}
                />
                <Box
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={borderColor}
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }}>
                        <Box p={4} borderRight={{ md: "1px solid" }} borderColor={borderColor}>
                            <Flex align="center" gap={2} mb={3}>
                                <Box w="4px" h="20px" bg="blue.400" borderRadius="full" />
                                <Text fontWeight="semibold" color={textColor}>Mínimas</Text>
                            </Flex>
                            <Ranking stations={rankings?.year_min} />
                        </Box>
                        <Box p={4}>
                            <Flex align="center" gap={2} mb={3}>
                                <Box w="4px" h="20px" bg="red.400" borderRadius="full" />
                                <Text fontWeight="semibold" color={textColor}>Máximas</Text>
                            </Flex>
                            <Ranking stations={rankings?.year_max} />
                        </Box>
                    </SimpleGrid>
                </Box>
            </Box>

            {/* Rain Ranking Section */}
            <Box mb={8}>
                <SectionHeader
                    icon={FaTint}
                    title="Ranking de Chuva"
                    subtitle="Acumulado diário"
                    color="#3182CE"
                />
                <Box
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={borderColor}
                    p={4}
                >
                    <RankingRain stations={rankings?.rain} />
                </Box>
            </Box>

            <Footer />
        </Box>
    );
}
